import { fetchAuthSession } from 'aws-amplify/auth'
import { sendSomethingWrongNotification } from 'helpers/sendSomethingWrongNotification'
import LocalStorage from 'utils/localStorage'

import { ApolloClient, ApolloLink, HttpLink, InMemoryCache, Observable } from '@apollo/client/core'
import { setContext } from '@apollo/client/link/context'
import { onError } from '@apollo/client/link/error'

const authLink = setContext((_, { headers }) => {
  const token = LocalStorage.token
  if (token) {
    return {
      headers: {
        ...headers,
        Authorization: token,
      },
    }
  }
  return {
    headers,
  }
})

/** The `sub` (user id) claim of a JWT, or undefined if it cannot be read. */
function subOf(token: string): string | undefined {
  try {
    const payload = token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/')
    return JSON.parse(window.atob(payload)).sub
  } catch {
    return undefined
  }
}

async function refreshToken() {
  const tokenAtStart = LocalStorage.token
  let idToken
  try {
    // Uses the refresh token Amplify holds for the signed-in user. Stores the *id* token, as v5 did
    // here (sign-in stores the access token; the backend accepts either).
    const { tokens } = await fetchAuthSession({ forceRefresh: true })
    idToken = tokens?.idToken
    if (!idToken) {
      throw new Error('No session to refresh')
    }
  } catch (error) {
    console.error('[Auth] Unable to refresh token:', error)
    // Only clear the token this refresh was for, never one a newer sign-in has written since.
    if (LocalStorage.token === tokenAtStart) {
      LocalStorage.token = null
    }
    throw error
  }

  // Someone signed out, or a different user signed in, while this refresh was in flight. Writing the
  // refreshed token now would put the previous user's identity on the new user's requests.
  const current = LocalStorage.token
  if (current !== tokenAtStart && (current === null || subOf(current) !== idToken.payload.sub)) {
    throw new Error('[Auth] Session changed during token refresh')
  }

  LocalStorage.token = idToken.toString()
  return LocalStorage.token
}

const errorLink = onError(({ graphQLErrors, networkError, operation, forward }) => {
  if (networkError) {
    sendSomethingWrongNotification()
    console.error(`[Network error]: ${networkError}`)
  }
  if (graphQLErrors) {
    for (const graphQLError of graphQLErrors) {
      const { message, path } = graphQLError
      if (message === 'jwt expired') {
        return new Observable((observer) => {
          refreshToken()
            .then((token) => {
              operation.setContext({
                headers: {
                  ...operation.getContext().headers,
                  Authorization: token,
                },
              })

              const subscriber = {
                next: observer.next.bind(observer),
                error: observer.error.bind(observer),
                complete: observer.complete.bind(observer),
              }

              forward(operation).subscribe(subscriber)
            })
            .catch((error: Error) => {
              observer.error(error)
            })
        })
      } else {
        sendSomethingWrongNotification()
        console.error(`[GraphQL error]: Message: ${message}, Path: ${path}`)
      }
    }
  }

  return
})

const httpLink = new HttpLink({
  fetchOptions: { mode: 'cors' },
  uri: `${process.env.REACT_APP_API_URL}`,
})

export const apolloClient = new ApolloClient({
  connectToDevTools: true,
  cache: new InMemoryCache({
    typePolicies: {
      UserOutputDto: { keyFields: ['userId'] },
      SeriesOutputDto: { keyFields: ['seriesId'] },
      ShiurimOutputDto: { keyFields: ['shiurId'] },
      ShiurimHistoryOutputDto: { keyFields: ['shiurId'] },
      BookmarkOutputDto: { keyFields: ['bookmarkId'] },
      NoteOutputDto: { keyFields: ['noteId'] },
    },
  }),
  link: ApolloLink.from([errorLink, authLink, httpLink]),
})
